// Range-based pagination for Supabase/PostgREST reads.
//
// PostgREST silently caps a single response at `max-rows` (1,000 by default on
// Supabase). Any read whose row count grows with the data (e.g. actuals = rows
// per customer × hub × day) will be SILENTLY TRUNCATED past that cap. This helper
// loops `.range(from, to)` pages until a short page comes back, so callers get the
// complete result regardless of size.
//
// IMPORTANT: the query passed in MUST have a stable, total ordering (e.g.
// `.order("id")`). Without it, PostgREST row order is undefined and paging can
// skip or duplicate rows across page boundaries.

export const PAGE_SIZE = 1000;

/**
 * Fetch every row of a query by paging through `.range()`.
 *
 * @param makePage builds a fresh PostgREST query for one page. It receives the
 *   inclusive `from`/`to` row offsets and must call `.range(from, to)` on an
 *   already-ordered query. A fresh builder is required each call because a
 *   PostgREST builder is single-use once awaited.
 */
export async function fetchAllPaged<T>(
  makePage: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>
): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await makePage(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    const batch = data ?? [];
    out.push(...batch);
    if (batch.length < PAGE_SIZE) break;
  }
  return out;
}
